import { ConversationMessage, Investigation, InvestigationSummary, InvestigationScope } from '../../../types';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import { useMemo } from 'react';

export const createInvestigationService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  const service = {
    getInvestigationsForCustomer: async (customerId: string): Promise<InvestigationSummary[]> => {
      return rpcCall(client, 'investigations.ListInvestigations', { customer_id: customerId });
    },

    getInvestigationById: async (customerId: string, investigationId: string): Promise<Investigation> => {
      return rpcCall(client, 'investigations.GetInvestigation', { customer_id: customerId, investigation_id: investigationId });
    },

    createInvestigation: async (
      customerId: string,
      investigation: {
        title?: string;
        description?: string;
        scope?: InvestigationScope[];
        playbook?: {
          id: string;
          version: string;
          variables?: Record<string, string>;
        };
      }
    ): Promise<Investigation> => {
      return rpcCall(client, 'investigations.CreateInvestigation', {
        customer_id: customerId,
        // No title unless the caller gave one. The aggregate defaults to 'Untitled
        // Investigation', which is the exact string the agent's naming tool treats as "not named
        // yet"; sending any other wording here is what kept a case from ever naming itself from
        // its first message.
        ...(investigation.title ? { title: investigation.title } : {}),
        description: investigation.description || 'Security investigation',
        scope: investigation.scope || [{ accounts: [] }],
        state: 'active',
        playbook: investigation.playbook,
      });
    },

    setPlaybook: async (
      customerId: string,
      investigationId: string,
      playbook: { id: string; version: string; owner: string } | null,
    ): Promise<Investigation> =>
      rpcCall(client, 'investigations.SetInvestigationPlaybook', {
        customer_id: customerId,
        investigation_id: investigationId,
        playbook,
      }),

    updateInvestigation: async (
      customerId: string,
      investigationId: string,
      updates: Partial<Investigation>
    ): Promise<Investigation> => {
      return rpcCall(client, 'investigations.UpdateInvestigation', { customer_id: customerId, investigation_id: investigationId, ...updates });
    },

    deleteInvestigation: async (customerId: string, investigationId: string): Promise<void> => {
      await rpcCall(client, 'investigations.DeleteInvestigation', { customer_id: customerId, investigation_id: investigationId });
    },

    updateInvestigationStatus: async (
      customerId: string,
      investigationId: string,
      state: 'active' | 'completed' | 'archived'
    ): Promise<Investigation> => {
      return service.updateInvestigation(customerId, investigationId, { state });
    },

    // History returns ConversationMessage (the unified streaming/history shape),
    // not the Message aggregate — typing it as Message hid token_usage from
    // every consumer.
    getConversationHistory: async (customerId: string, investigationId: string): Promise<ConversationMessage[]> => {
      const result = await rpcCall<{ messages?: ConversationMessage[] }>(client, 'investigations.GetConversation', { customer_id: customerId, investigation_id: investigationId });
      return result.messages || [];
    },

    markPlaybookExecuted: async (customerId: string, investigationId: string): Promise<Investigation> => {
      return rpcCall<Investigation>(client, 'investigations.ExecutePlaybook', { customer_id: customerId, investigation_id: investigationId });
    },

    clearConversation: async (customerId: string, investigationId: string): Promise<void> => {
      await rpcCall(client, 'investigations.ClearConversation', { customer_id: customerId, investigation_id: investigationId });
    }
  };

  return service;
};

export const useInvestigationService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('Not authenticated');
    }

    return createInvestigationService(idToken);
  }, [idToken]);
};
